import { CampusDestinations } from './destinationAvailability';
import { MovementTaskEditScope } from './roleHelper';

/**
 * Editing where an open movement task takes its trailer from and to (flux
 * `editMovementTask`). The server re-checks all of this; these rules only decide what
 * the Task App offers.
 */

export type LocationKind = 'yard_spot' | 'dock' | 'facility_zone';

export interface LocationChoice {
  kind: LocationKind;
  uuid: string;
}

export interface LocationOption extends LocationChoice {
  label: string;
  /** Zone or warehouse zone the place is in, shown beside it. */
  detail: string;
  facilityId: string;
  facilityName: string;
}

/** The movement types flux can edit: moves between yard spots, docks and zones. */
export const EDITABLE_MOVEMENT_TYPES = [
  'yard_to_yard',
  'yard_to_warehouse',
  'yard_spot_to_facility_zone',
  'warehouse_to_yard',
  'warehouse_to_warehouse',
  'dock_to_facility_zone',
  'facility_zone_to_yard_spot',
  'facility_zone_to_dock',
  'facility_zone_to_facility_zone'
];

const KIND_BY_MODEL: Record<string, LocationKind> = {
  YardSpot: 'yard_spot',
  Dock: 'dock',
  FacilityZone: 'facility_zone'
};

interface EditableTask {
  data?: any;
  task_type?: { type?: string } | null;
  trailer?: { status_name?: string | null } | null;
}

/**
 * Whether the edit button shows for `task`: an empty trailer's in-yard move, for a
 * user whose scope covers it. On the Available tab tasks are unassigned, so only
 * scope 'any' may edit them; My Tasks are the user's own.
 */
export function canEditMovementTask(
  task: EditableTask,
  scope: MovementTaskEditScope,
  isOwnTask: boolean
): boolean {
  if (scope === 'none' || (scope === 'own' && !isOwnTask)) return false;
  if (task.task_type?.type !== 'MOVE_TRAILER') return false;
  if (!EDITABLE_MOVEMENT_TYPES.includes(task.data?.movement_type)) return false;
  return (task.trailer?.status_name || '').toUpperCase() === 'EMPTY';
}

/** The task's stored end (`from` or `to`), when it is a yard spot, dock or zone. */
export function storedLocation(data: any, side: 'from' | 'to'): LocationChoice | null {
  const kind = KIND_BY_MODEL[data?.[`${side}_model`]];
  const uuid = data?.[`${side}_uuid`];
  return kind && uuid ? { kind, uuid } : null;
}

export function sameLocation(
  a: LocationChoice | null,
  b: LocationChoice | null
): boolean {
  if (!a || !b) return a === b;
  return a.kind === b.kind && a.uuid === b.uuid;
}

interface OptionContext {
  /** The trailer being moved: its own place is never "occupied" for it. */
  trailerUuid?: string | null;
  /** Only places at this facility (the picker's facility), else the whole campus. */
  facilityId?: string | null;
  /** Always offered, even when it would be filtered out (the task's current end). */
  current?: LocationChoice | null;
  /** Never offered (the other end of the move). */
  exclude?: LocationChoice | null;
}

/**
 * The places offered for one end of the move.
 *
 * Destination: an empty, unlocked, available spot; an unlocked door that is empty or
 * whose trailer is already moving out (the backfill flux allows); any zone.
 * Source: any spot or door in the campus not holding another trailer, and not locked by
 * another move; any zone. The task's current end is always offered:
 * its own lock makes it look taken.
 */
export function locationOptions(
  destinations: Pick<CampusDestinations, 'yardSpots' | 'dockDoors' | 'facilityZones'>,
  kind: LocationKind,
  side: 'source' | 'destination',
  context: OptionContext
): LocationOption[] {
  const { trailerUuid, facilityId, current, exclude } = context;
  const otherTrailer = (uuid?: string | null) => !!uuid && uuid !== trailerUuid;
  const atFacility = (id: string) => !facilityId || String(id) === String(facilityId);
  const isCurrent = (uuid: string) => current?.kind === kind && current.uuid === uuid;
  const isExcluded = (uuid: string) => exclude?.kind === kind && exclude.uuid === uuid;

  let options: LocationOption[] = [];

  if (kind === 'yard_spot') {
    options = destinations.yardSpots
      .filter((spot) => atFacility(spot.facilityId))
      .filter(
        (spot) =>
          isCurrent(spot.yard_spot_uuid) ||
          (!spot.isLocked &&
            !otherTrailer(spot.trailer_uuid) &&
            (side === 'source' || spot.configuration?.status !== 'unavailable'))
      )
      .map((spot) => ({
        kind,
        uuid: spot.yard_spot_uuid,
        label: spot.yard_spot_name,
        detail: spot.zoneName,
        facilityId: spot.facilityId,
        facilityName: spot.facilityName
      }));
  } else if (kind === 'dock') {
    options = destinations.dockDoors
      .filter((door) => atFacility(door.facilityId))
      .filter(
        (door) =>
          isCurrent(door.dockUuid) ||
          (!door.isLocked &&
            (side === 'source'
              ? !otherTrailer(door.trailer_uuid)
              : !door.trailer_uuid || door.hasMoveFromTask))
      )
      .map((door) => ({
        kind,
        uuid: door.dockUuid,
        label: door.dockName,
        detail: door.zoneName,
        facilityId: door.facilityId,
        facilityName: door.facilityName
      }));
  } else {
    options = destinations.facilityZones
      .filter((zone) => atFacility(zone.facilityId))
      .map((zone) => ({
        kind,
        uuid: zone.facility_zone_uuid,
        label: zone.facility_zone_name,
        detail: '',
        facilityId: zone.facilityId,
        facilityName: zone.facilityName
      }));
  }

  return options.filter((option) => !isExcluded(option.uuid));
}

const INPUT_KEY: Record<LocationKind, string> = {
  yard_spot: 'yardSpotUuid',
  dock: 'dockUuid',
  facility_zone: 'facilityZoneUuid'
};

/**
 * The `editMovementTask` input: only the ends that changed are sent, and the facility the
 * task is created for only when it changed.
 */
export function buildEditInput(
  taskUuid: string,
  currentSource: LocationChoice | null,
  currentDestination: LocationChoice | null,
  source: LocationChoice | null,
  destination: LocationChoice | null,
  currentCreatedByFacilityId?: string | null,
  createdByFacilityId?: string | null
): Record<string, any> {
  const input: Record<string, any> = { taskUuid };
  if (source && !sameLocation(source, currentSource)) {
    input.source = { [INPUT_KEY[source.kind]]: source.uuid };
  }
  if (destination && !sameLocation(destination, currentDestination)) {
    input.destination = { [INPUT_KEY[destination.kind]]: destination.uuid };
  }
  if (createdByFacilityId && createdByFacilityId !== currentCreatedByFacilityId) {
    input.createdByFacilityId = createdByFacilityId;
  }
  return input;
}
